/**
 * ConversationSoundService — phát âm thanh thông báo tin nhắn mới.
 *
 * Port từ FE13 `conversation.service.ts`:
 * - `alertAudioNew`: object { message, comment } — cài đặt âm thanh kiểu mới (riêng message/comment)
 * - `alertAudio`: HTMLAudioElement — cài đặt kiểu cũ (1 âm thanh chung)
 * - `playAlert(type, isMuted)`: phát âm thanh nếu chưa mute
 *
 * Audio settings được load từ `auth.settings.notifications_sounds`.
 */
import {inject, Injectable} from '@angular/core';
import {AuthService} from '@core/authentication/auth.service';
import {environment} from '@environments/environment';
import {CONVERSATION_TYPE} from '../models/conversation.model';
import {UserSettingsService} from '@shared/services/global/user-settings.service';

/** Trạng thái bật/tắt âm thanh thông báo */
export const SOUND_STATUS = {
  ON: 1,
  OFF: 2,
} as const;

const notificationSoundBaseUrl = environment.cloudFlareService?.isActiveDomainStatic
  && environment.cloudFlareService.url.static
  ? `${environment.cloudFlareService.url.static.replace(/\/$/, '')}/assets/sound/`
  : 'assets/sound/';

const soundFileUrl = (fileName: string): string => `${notificationSoundBaseUrl}${fileName}.mp3`;

export const NOTIFICATION_SOUND: Record<string, { name: string; fileUrl: string }> = {
  'default': {name: 'Mặc định', fileUrl: soundFileUrl('default')},
  'messenger': {name: 'Messenger', fileUrl: soundFileUrl('messenger')},
  'skype': {name: 'Skype', fileUrl: soundFileUrl('skype')},
  'yahoo': {name: 'Yahoo', fileUrl: soundFileUrl('yahoo')},
  'telegram': {name: 'Telegram', fileUrl: soundFileUrl('telegram')},
  'nokia': {name: 'Nokia', fileUrl: soundFileUrl('nokia')},
  'samsung': {name: 'Samsung', fileUrl: soundFileUrl('samsung')},
  'iphone': {name: 'iPhone', fileUrl: soundFileUrl('iphone')},
};

@Injectable({providedIn: 'root'})
export class ConversationSoundService {
  private readonly auth = inject(AuthService);
  private readonly userSettings = inject(UserSettingsService);

  /** Cài đặt âm thanh kiểu mới: { message: Audio | null, comment: Audio | null } */
  private alertAudioNew: { message: HTMLAudioElement | null; comment: HTMLAudioElement | null } | null = {
    message: null,
    comment: null,
  };

  /** Cài đặt âm thanh kiểu cũ: 1 audio chung */
  private alertAudio: HTMLAudioElement | null = null;

  /** Đã load settings thành công ít nhất 1 lần — tránh ghi đè state đúng bằng state mặc định */
  private _settingsLoaded = false;

  /**
   * Cờ phát âm thanh dùng chung, được cập nhật khi setting thay đổi.
   * `playAlert()` chỉ đọc state này, tuyệt đối không đọc localStorage/API.
   */
  private readonly soundEnabled = {message: false, comment: false};

  constructor() {
    // Settings là nguồn sự thật duy nhất sau khi API trả về. Subscription này
    // giúp đổi setting có hiệu lực tức thì, kể cả trang hội thoại chưa mount.
    this.userSettings.settings$.subscribe(settings => {
      if (settings === null) {
        this.soundEnabled.message = false;
        this.soundEnabled.comment = false;
        return;
      }
      this.loadSettings();
    });
  }

  /**
   * Khởi tạo audio settings từ thông tin user.
   * Gọi sau khi login / settings thay đổi.
   * Idempotent: có thể gọi nhiều lần, lần sau override lần trước.
   */
  public loadSettings(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const authData = (this.auth as any).auth ?? {};
    // UserSettingsService là nguồn chuẩn sau khi `/user/setting` đã load.
    // Auth chỉ là snapshot khởi động trước lúc API trả về. Không được ưu tiên
    // auth ở đây, vì snapshot đó từng khiến toggle đã lưu nhưng âm thanh vẫn
    // chạy theo setting cũ trong cùng phiên.
    const settings = this.userSettings.setting() ?? authData.settings ?? {};
    const stSounds = settings.notifications_sounds;

    // ── Try new format (message/comment riêng) ──────────────────────────
    const soundSrc = stSounds as { message?: { on?: number | string; sound?: string }; comment?: { on?: number | string; sound?: string } } | undefined;
    // Có object notifications_sounds nghĩa là user đã dùng format mới, kể cả
    // khi cả message và comment đều OFF. Parity FE13: không fallback sang
    // notification kiểu cũ trong trường hợp này.
    const hasNewFormat = !!soundSrc && typeof soundSrc === 'object'
      && (Object.prototype.hasOwnProperty.call(soundSrc, 'message')
        || Object.prototype.hasOwnProperty.call(soundSrc, 'comment'));
    if (hasNewFormat) {
      this.soundEnabled.message = Number(soundSrc?.message?.on) === SOUND_STATUS.ON;
      this.soundEnabled.comment = Number(soundSrc?.comment?.on) === SOUND_STATUS.ON;
      // Đảm bảo alertAudioNew là object (có thể đã bị null từ lần loadSettings trước)
      if (!this.alertAudioNew) {
        this.alertAudioNew = {message: null, comment: null};
      }
      this.initAudio('message', soundSrc?.message);
      this.initAudio('comment', soundSrc?.comment);
      // Xoá audio cũ để tránh chơi cả 2
      this.alertAudio = null;
      this._settingsLoaded = true;
      return;
    }

    // ── Try old format (1 audio chung) ─────────────────────────────────
    const st = settings.notifications ?? this.userSettings.setting()?.notifications;
    if (Number(st?.on) === SOUND_STATUS.ON) {
      this.soundEnabled.message = true;
      this.soundEnabled.comment = true;
      const url = NOTIFICATION_SOUND[st.sound]?.fileUrl;
      if (url && (!this.alertAudio || this.alertAudio.src !== url)) {
        this.alertAudio = new Audio(url);
      }
      this.alertAudioNew = null;
      this._settingsLoaded = true;
      return;
    }

    // ── Fallback: không có cài đặt nào → init default sound ──────────
    this.soundEnabled.message = false;
    this.soundEnabled.comment = false;
    if (!this._settingsLoaded) {
      this.alertAudio = new Audio(NOTIFICATION_SOUND['default'].fileUrl);
    }
  }

  /**
   * Phát âm thanh thông báo tin nhắn mới.
   * FE13: `conversation.service.ts playAlert()`.
   *
   * @param type Loại hội thoại: CONVERSATION_TYPE.COMMENT (1) hoặc INBOX (2)
   * @param isMuted true nếu hội thoại đang tắt thông báo
   */
  public playAlert(type: number = CONVERSATION_TYPE.INBOX, isMuted: boolean = false): void {
    // Tắt thông báo hội thoại thì không bật âm thanh
    const soundType = type === CONVERSATION_TYPE.COMMENT ? 'comment' : 'message';
    if (isMuted || !this.soundEnabled[soundType]) return;

    if (this.alertAudioNew) {
      // Cài đặt kiểu mới — âm thanh riêng message/comment
      if (type === CONVERSATION_TYPE.COMMENT) {
        if (this.alertAudioNew.comment) {
          this.alertAudioNew.comment.play().catch(() => 0);
          return;
        }
      } else {
        if (this.alertAudioNew.message) {
          this.alertAudioNew.message.play().catch(() => 0);
          return;
        }
      }
    }

    // Cài đặt kiểu cũ — 1 âm thanh chung
    if (this.alertAudio) {
      this.alertAudio.play().catch(() => 0);
      return;
    }

    // Fallback: chưa có settings nào → init default và phát
    const defaultUrl = NOTIFICATION_SOUND['default']?.fileUrl;
    if (defaultUrl) {
      this.alertAudio = new Audio(defaultUrl);
      this.alertAudio.play().catch(() => 0);
    }
  }

  private initAudio(type: 'message' | 'comment', config: { on?: number | string; sound?: string } | undefined): void {
    if (Number(config?.on) === SOUND_STATUS.ON) {
      const url = NOTIFICATION_SOUND[config?.sound ?? 'default']?.fileUrl;
      if (url && this.alertAudioNew) {
        if (!this.alertAudioNew[type] || this.alertAudioNew[type]?.src !== url) {
          this.alertAudioNew[type] = new Audio(url);
        }
      }
    } else if (this.alertAudioNew) {
      this.alertAudioNew[type] = null;
    }
  }

}
